'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';

import { paths } from 'src/routes/paths';

import { SplashScreen } from 'src/components/loading-screen';

/** Matches CONFIG.auth.redirectPath; trailing slash for static-export routing consistency */
const DEFAULT_APP_PATH = `${paths.dashboard.projects.root}/`;

// ----------------------------------------------------------------------

const VALID_DASHBOARD_SECTIONS = [
  'projects', 'activities', 'clients', 'standards', 'service-types',
  'solutions', 'member', 'organization', 'competencies', 'schedule',
  'reports', 'account', 'roles', 'configuration', 'holidays', 'leave',
  'file-import', 'tasks', 'competency-categories', 'standard-categories',
  'accreditation-bodies', 'client-types', 'file-manager', 'permission',
  'programmes',
];

const VALID_TOP_LEVEL = [
  '/client', '/competency', '/auth',
  '/error/403', '/error/404', '/error/500',
  '/coming-soon', '/maintenance', '/payment',
  '/about-us', '/contact-us', '/faqs',
  '/createCompanyName', '/dashboard',
];

// SPA catch-all bases — these routes use [[...slug]] and need special handling
const SPA_CATCH_ALL_BASES = [
  '/dashboard/projects/', '/dashboard/activities/', '/dashboard/clients/',
  '/dashboard/standards/', '/dashboard/service-types/', '/dashboard/solutions/',
  '/dashboard/member/', '/dashboard/organization/users/', '/dashboard/competencies/',
  '/dashboard/programmes/',
  '/client/', '/competency/',
];

function needsFullReload(path: string): boolean {
  const clean = path.endsWith('/') ? path : `${path}/`;
  const match = SPA_CATCH_ALL_BASES.find((base) => clean.startsWith(base) && clean.length > base.length);
  if (!match) return false;

  const subPath = clean.slice(match.length);
  const firstSegment = subPath.split('/')[0];

  // Allow concrete nested routes that have their own pages (no SPA catch-all)
  if (
    (match === '/dashboard/standards/' && firstSegment === 'new') ||
    (match === '/dashboard/projects/' && firstSegment === 'new') ||
    (match === '/dashboard/clients/' && firstSegment === 'categories') ||
    (match === '/dashboard/programmes/' && (firstSegment === 'new' || firstSegment === 'edit'))
  ) {
    return false;
  }

  return true;
}

function isKnownRoute(path: string): boolean {
  const clean = path.replace(/\/+$/, '') || '/';
  if (clean === '/' || clean === '/dashboard') return true;
  if (clean.startsWith('/dashboard/')) {
    const section = clean.slice('/dashboard/'.length).split('/')[0];
    return VALID_DASHBOARD_SECTIONS.includes(section);
  }
  return VALID_TOP_LEVEL.some((prefix) => clean === prefix || clean.startsWith(`${prefix}/`));
}

export default function Page() {
  const router = useRouter();

  useEffect(() => {
    const path = window.location.pathname;

    async function init() {
      // Register service worker only in production (static export)
      if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
        try {
          await navigator.serviceWorker.register('/sw.js');
          if (!navigator.serviceWorker.controller) {
            await new Promise<void>((resolve) => {
              navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
              setTimeout(resolve, 3000);
            });
          }
        } catch (e) {
          console.warn('Service worker registration failed:', e);
        }
      }

      // Navigate to the correct page
      if (path === '/' || path === '/index.html') {
        router.replace(DEFAULT_APP_PATH);
      } else if (isKnownRoute(path)) {
        if (needsFullReload(path)) {
          // /dashboard/member/{id}/holidays → redirect to the stable /dashboard/leave/ page
          if (/^\/dashboard\/member\/\d+\/holidays\/?$/.test(path)) {
            router.replace(`${paths.dashboard.leave.root}/`);
            return;
          }
          const base = SPA_CATCH_ALL_BASES.find((b) => path.startsWith(b));
          router.replace(base || DEFAULT_APP_PATH);
        } else {
          router.replace(path);
        }
      } else {
        window.location.replace('/error/404/');
      }
    }

    init().catch((e) => console.error('Init failed:', e));
  }, [router]);

  return <SplashScreen />;
}
